import React, { MouseEvent, useState } from 'react'
import { Theme } from '@mui/material'

import {
  isDashboardWidgetFullyLoaded,
  ParsedSegmentPath,
  ParsedWidgetDataLabel,
  TCoreWidgetFullyLoaded
} from 'types/GlobalWidget'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableRow from 'components_new/atoms/Table/TableRow'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableHead from 'components_new/atoms/Table/TableHead'
import Text from 'components_new/atoms/Text'

import TrendText from 'components_new/molecules/TrendText'

import SegmentMenu from 'components_new/organisms/Widget/SegmentMenu'

import Marker from '../Marker'
import { useTranslation } from 'language/translator'

interface SingleProps {
  differences: (number | null)[]
  kpis: string[]
  theme: Theme
  label: ParsedWidgetDataLabel
  units: (string | null)[]
  values: number[]
  percentages: (number | null)[]
  increaseIsPositives: boolean[]
  setDetailsDialog: ((segment: ParsedSegmentPath[]) => void) | null
  setSegmentFilters: ((segment: ParsedSegmentPath[]) => void) | null
  widget: TCoreWidgetFullyLoaded
}

const Single = (props: SingleProps) => {
  const {
    differences,
    increaseIsPositives,
    kpis,
    label,
    percentages,
    setDetailsDialog,
    setSegmentFilters,
    theme,
    units,
    values,
    widget
  } = props

  const { t } = useTranslation()

  const [anchorPosition, setAnchorPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const allowOnClick = !!setSegmentFilters || !!setDetailsDialog

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!allowOnClick) return

    event.stopPropagation()
    setAnchorPosition({ left: event.clientX, top: event.clientY })
  }

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorPosition(null)
  }

  const showComparative =
    percentages.length > 0 && percentages.some((value) => value !== null)

  return (
    <>
      <Marker
        body={
          <Table noDividers={true} size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ py: 0, pl: 0.5 }}>
                  {''}
                </TableCell>
                <TableCell align="right" sx={{ py: 0, pl: 0.5 }}>
                  <Text
                    color="text.primary"
                    component="span"
                    noWrap={true}
                    variant="caption"
                  >
                    {t('global', 'total')}
                  </Text>
                </TableCell>
                {showComparative ? (
                  <>
                    <TableCell align="right" sx={{ py: 0 }}>
                      <Text
                        color="text.primary"
                        component="span"
                        noWrap={true}
                        variant="caption"
                      >
                        {t('global', 'compare_with_short', true)}
                      </Text>
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {values !== undefined &&
                kpis.map((kpi, i) => (
                  <TableRow key={kpi}>
                    <TableCell align="left" sx={{ py: 0, pl: 0.5 }}>
                      <Text
                        color="text.primary"
                        component="span"
                        fontWeight="medium"
                        noWrap={true}
                        variant="body2"
                      >
                        {kpi}
                      </Text>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0, pl: 0.5 }}>
                      <Text
                        color="text.primary"
                        component="span"
                        fontWeight="medium"
                        variant="body2"
                      >
                        {values[i]}
                      </Text>
                      {units[i] ? (
                        <Text
                          color="text.secondary"
                          component="span"
                          sx={{
                            ml: '0.25em'
                          }}
                          variant="caption"
                        >
                          {units[i]}
                        </Text>
                      ) : null}
                    </TableCell>
                    {showComparative ? (
                      <>
                        <TableCell align="right" sx={{ py: 0 }}>
                          {percentages[i] !== null ? (
                            <TrendText
                              difference={differences[i] as number}
                              increaseIsPositive={increaseIsPositives[i]}
                              // numberOfDecimals={numberOfDecimals[i]} // @TODO
                              numberOfDecimals={0}
                              percentage={percentages[i] as number}
                            />
                          ) : (
                            ''
                          )}
                        </TableCell>
                      </>
                    ) : null}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        }
        theme={theme}
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Icon color="inherit" fontSize="small" name="DomainOutlined" />
            <Text
              onClick={(event) => handleClick(event)}
              variant="body2"
              noWrap={true}
              sx={{
                color: 'common.white',
                cursor: allowOnClick ? 'pointer' : undefined,
                textDecoration: 'underline',
                '&:hover': allowOnClick
                  ? {
                      textDecoration: 'none'
                    }
                  : undefined
              }}
            >
              {label.display_label}
            </Text>
          </Box>
        }
      />

      {/* ----- Menu ----- */}
      {allowOnClick && isDashboardWidgetFullyLoaded(widget) && (
        <SegmentMenu
          anchorPosition={anchorPosition}
          dashboardId={widget.dashboard_id}
          setDetailsDialog={setDetailsDialog}
          setFilters={setSegmentFilters}
          onClose={handleClose}
          segments={[label]}
          widget={widget}
        />
      )}
    </>
  )
}

export default Single
